import {
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import { tap, withLatestFrom } from 'rxjs/operators';
import { isPptxExportRunning, pptxExport$ } from '../pptxExport/pptxExport$';
import {
  backToSlide,
  navigatorTourStarted,
  nextSlide,
  previousSlide,
  reloadCurrentSlide,
  selectNodeInPresentationMode,
  selectSlide,
  setIsNavigatorDismissed,
  setNodeDescription,
  setSlideDescription,
  setSlideNavigatorState,
} from './actions';
import { slideNavigator$ } from './slideNavigator$';
import { presentationSlides$ } from './presentationSlides$';
import {
  setStoredHasSeenNavigatorTour,
  setStoredIsNavigatorDismissed,
} from './navigatorPreferences';
import { PersonalSetting } from '@ardoq/api-types';
import { currentUser$ } from 'streams/currentUser/currentUser$';
import { updatePersonalSetting } from 'streams/currentUser/actions';
import { logError } from '@ardoq/logging';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import {
  setSlidesMigrationInfo,
  toggleExploreMode,
} from 'presentation/actions';
import {
  loadSlide,
  loadSlideError,
  type PayloadLoadSlide,
} from 'presentation/viewPane/actions';
import { trackViewedSlidePresentationMode } from 'presentation/tracking';
import { LoadSlideErrorTypes } from 'presentation/viewPane/types';
import {
  type ArdoqId,
  type PersistedMetamodel,
  SlideTypes,
} from '@ardoq/api-types';
import { displayCountdownForDeprecatedSlides } from 'viewDeprecation/viewDeprecationCountdownDialog';
import { isDiscontinuedSlide } from 'viewDeprecation/discontinuedSlides';
import {
  getNextSelectableSlideId,
  getPreviousSelectableSlideId,
} from './slideSelection';
import { deprecatedViews$ } from 'views/deprecatedViews$';
import { slideInterface } from '../../modelInterface/presentations/slideInterface';
import { slideOperations } from 'streams/slides/slideOperations';
import { slides$ } from 'streams/slides/slides$';
import { metamodels$ } from 'streams/metamodels/metamodels$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { loadedPresentation$ } from 'presentation/streams/loadedPresentation$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

const handleNextSlide = routine(
  ofType(nextSlide),
  withLatestFrom(slideNavigator$, presentationSlides$, deprecatedViews$),
  tap(([_, { selectedSlideId }, slides, deprecatedViews]) => {
    const slideIdToSelect = getNextSelectableSlideId({
      slides,
      selectedSlideId,
      deprecatedViews,
    });

    if (slideIdToSelect) {
      dispatchAction(selectSlide(slideIdToSelect));
    }
  })
);

const handlePreviousSlide = routine(
  ofType(previousSlide),
  withLatestFrom(slideNavigator$, presentationSlides$, deprecatedViews$),
  tap(([_, { selectedSlideId }, slides, deprecatedViews]) => {
    const slideIdToSelect = getPreviousSelectableSlideId({
      slides,
      selectedSlideId,
      deprecatedViews,
    });

    if (slideIdToSelect) {
      dispatchAction(selectSlide(slideIdToSelect));
    }
  })
);

const handleBackToSlide = routine(
  ofType(backToSlide),
  withLatestFrom(slideNavigator$),
  tap(([_, { selectedSlideId }]) => {
    if (!selectedSlideId) return;
    const payload = slideInterface.getSlideLoadPayload(selectedSlideId);

    if (payload) {
      dispatchAction(loadSlide(payload));
      dispatchAction(setSlideDescription(selectedSlideId));
    } else {
      dispatchAction(selectSlide(null));
      logError(Error('Active slide was not found'), null, {
        activeSlideId: selectedSlideId,
      });
    }
  })
);

const handlePersistNavigatorDismissedPreference = routine(
  ofType(setIsNavigatorDismissed),
  extractPayload(),
  tap(setStoredIsNavigatorDismissed)
);

const handlePersistHasSeenNavigatorTour = routine(
  ofType(navigatorTourStarted),
  withLatestFrom(currentUser$),
  tap(([, currentUser]) => {
    setStoredHasSeenNavigatorTour(true);
    if (!currentUser) return;
    dispatchAction(
      updatePersonalSetting({
        personalSetting: PersonalSetting.HAS_SEEN_PRESENTATION_NAVIGATOR_TOUR,
        newValue: true,
      })
    );
  })
);

const getNameAndDescription = (id: ArdoqId, i18nLocale: I18nLocale) => {
  if (componentInterface.isComponent(id)) {
    return {
      name:
        componentInterface.getRawLabel(id) ??
        t(
          i18nLocale,
          'presentationAdmin:viewer.slideNavigator.unknownComponent',
          'Unknown component'
        ),
      description: componentInterface.get(id)?.description ?? '',
    };
  }
  if (referenceInterface.isReference(id)) {
    return {
      name:
        referenceInterface.getDisplayLabel(id) ??
        t(
          i18nLocale,
          'presentationAdmin:viewer.slideNavigator.unknownReference',
          'Unknown reference'
        ),
      description: referenceInterface.get(id)?.description ?? '',
    };
  }
  logError(
    Error('selectNodeInPresentationMode: id is neither component nor reference')
  );
  return {
    name: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.unknownEntity',
      'Unknown entity'
    ),
    description: '',
  };
};

const handleSelectNode = routine(
  ofType(selectNodeInPresentationMode),
  extractPayload(),
  withLatestFrom(i18nLocale$),
  tap(([id, i18nLocale]) =>
    dispatchAction(setNodeDescription(getNameAndDescription(id, i18nLocale)))
  )
);

const handleToggleExploreMode = routine(
  ofType(toggleExploreMode),
  extractPayload(),
  withLatestFrom(slideNavigator$),
  tap(([{ isExploreMode }, { selectedSlideId }]) => {
    const wasToggledOff = !isExploreMode;
    if (wasToggledOff) dispatchAction(selectSlide(selectedSlideId));
  })
);

const rendersAnUnloadedMetamodel = (
  { type, metamodelId }: PayloadLoadSlide,
  metamodels: PersistedMetamodel[]
): boolean =>
  type === SlideTypes.METAMODEL &&
  !metamodels.some(metamodel => metamodel._id === metamodelId);

const handleSelectSlide = routine(
  ofType(selectSlide),
  extractPayload(),
  withLatestFrom(
    loadedPresentation$,
    currentUserPermissionContext$,
    slides$,
    metamodels$,
    pptxExport$,
    deprecatedViews$
  ),
  tap(
    ([
      slideID,
      { presentation },
      permissionContext,
      slides,
      metamodels,
      pptxExport,
      deprecatedViews,
    ]) => {
      const allSlideIDs = presentation?.slides ?? [];
      const parsedSlideID = slideID || allSlideIDs[0];
      if (!parsedSlideID) return;

      const payload = slideInterface.getSlideLoadPayload(parsedSlideID);
      if (!payload) {
        const slideExists = allSlideIDs.includes(parsedSlideID);
        if (slideExists) {
          // Slide exists but user is not authorized to see it
          dispatchAction(
            loadSlideError({ error: LoadSlideErrorTypes.UNAUTHORIZED })
          );
        }
        return;
      }

      if (rendersAnUnloadedMetamodel(payload, metamodels)) {
        logError(
          Error('The metamodel of a metamodel slide was not found'),
          null,
          {
            slideId: payload.id,
            metamodelId: payload.metamodelId,
          }
        );
        dispatchAction(loadSlideError({ error: LoadSlideErrorTypes.UNKNOWN }));
        return;
      }

      const slide = slides.byId[payload.id];
      // working under premise that a discontinued view can only be loaded from old slides
      // if the slide view is discontinued, we need to set the activeSlide in
      // slidesMigrationInfo$ for the view replacement to be shown
      dispatchAction(
        setSlidesMigrationInfo({
          activeSlide: isDiscontinuedSlide(slide, deprecatedViews)
            ? payload
            : undefined,
          activePresentation: presentation,
        })
      );

      dispatchAction(loadSlide(payload));
      dispatchAction(setSlideDescription(parsedSlideID));

      if (isPptxExportRunning(pptxExport)) {
        return;
      }
      displayCountdownForDeprecatedSlides({
        slide: payload,
        presentation: presentation ?? undefined,
        slideInfo: slide
          ? slideOperations.getSlideInfo(slide, permissionContext)
          : null,
        permissionContext,
        deprecatedViews,
      });
      trackViewedSlidePresentationMode({
        slideType: payload.type,
        viewId: payload.viewId,
        isScenario: payload.isScenario,
      });
    }
  )
);

const handleSetSlideDescription = routine(
  ofType(setSlideDescription),
  extractPayload(),
  tap(slideId => {
    if (!slideInterface.exists(slideId)) return;
    dispatchAction(
      setSlideNavigatorState({
        nodeDescription: null,
        slideDescription: slideInterface.getDescription(slideId),
        isNodeDescriptionExpanded: false,
      })
    );
  })
);

const handleSetNodeDescription = routine(
  ofType(setNodeDescription),
  extractPayload(),
  tap(({ name, description }) =>
    dispatchAction(
      setSlideNavigatorState({
        slideDescription: null,
        isNodeDescriptionExpanded: true,
        nodeDescription: {
          name,
          text: description,
        },
      })
    )
  )
);

const handleReloadSlide = routine(
  ofType(reloadCurrentSlide),
  withLatestFrom(slideNavigator$),
  tap(([_, { selectedSlideId }]) => {
    dispatchAction(selectSlide(selectedSlideId));
    const payload =
      selectedSlideId && slideInterface.getSlideLoadPayload(selectedSlideId);

    if (payload) {
      dispatchAction(loadSlide(payload));
    }
  })
);

export const routines = collectRoutines(
  handleNextSlide,
  handlePreviousSlide,
  handleBackToSlide,
  handlePersistNavigatorDismissedPreference,
  handlePersistHasSeenNavigatorTour,
  handleSelectNode,
  handleToggleExploreMode,
  handleSelectSlide,
  handleSetSlideDescription,
  handleSetNodeDescription,
  handleReloadSlide
);
